import type { ReactNode } from 'react'
import { Menu, toastManager } from '@systemeio/components'
import { Pin02Icon } from '@systemeio/icons'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import useUser from 'shared/hooks/use-user'
import { communityPostKeys } from 'modules/community/api/key-factory'
import { useUnpinPost } from 'modules/community/api/use-unpin-post'
import { mutatePostsByPin } from 'modules/community/components/pinned-post/utils'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import type { PostInterface } from 'modules/community/types/post-interface'
import type { PostPinnedToType } from 'modules/community/types/post-pinned-to-type'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'
import { isPinnedIn } from 'modules/community/utils/pinned-post'

interface PostPinnedHeaderProps {
  post: PostInterface
  className?: string
}

export const PostPinnedHeader = ({ post, className }: PostPinnedHeaderProps): ReactNode => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { query } = useRouter()
  const { communityPath, topicPath } = query
  const { user } = useUser()
  const { member } = useIsUserMember()
  const unpinMutation = useUnpinPost({ postId: post.id })
  const [isPostUnpinnable] = hasPermissions(user, 'posts', ['unpin'], { post, member })

  const handleUnpin = async (post: PostInterface, deleteIn: PostPinnedToType): Promise<void> => {
    try {
      await unpinMutation.trigger({ data: { source: deleteIn } })

      const shouldRemoveFromGlobal =
        (deleteIn === 'community' && !topicPath) || (deleteIn === 'topic' && !!topicPath)

      queryClient.setQueryData(
        communityPostKeys.pinnedList(communityPath, topicPath),
        mutatePostsByPin({
          post,
          shouldRemove: shouldRemoveFromGlobal,
          modifier: pinnedTo => pinnedTo.filter(pin => pin !== deleteIn),
        }),
      )

      if (shouldRemoveFromGlobal) {
        const listKey = topicPath
          ? communityPostKeys.topicList(communityPath as string, topicPath as string)
          : communityPostKeys.list(communityPath as string)

        queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
          { queryKey: listKey },
          old => {
            if (!old || !('pages' in old)) return old

            const firstPage = old.pages[0]
            if (firstPage && !firstPage.items.some(p => p.id === post.id)) {
              return {
                ...old,
                pages: [
                  {
                    ...firstPage,
                    items: [
                      { ...post, pinnedTo: post.pinnedTo.filter(p => p !== deleteIn) },
                      ...firstPage.items,
                    ],
                  },
                  ...old.pages.slice(1),
                ],
              }
            }
            return old
          },
        )

        queryClient.invalidateQueries({ queryKey: listKey })
      }

      toastManager.add({ type: 'success', title: t('community.actions.unpin_post.success') })
    } catch {
      toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
    }
  }

  return (
    <div
      className={cn(
        'flex h-[30px] items-center justify-between bg-bg-accent-active px-5',
        className,
      )}
    >
      <div className="flex items-center justify-center">
        <Pin02Icon className="size-5 text-text-on" />
        <span className="ml-2 text-[15px] font-semibold text-text-on">
          {t('pinned.post_pinned_title')}
        </span>
      </div>
      <div>
        {isPostUnpinnable && (
          <div className="relative">
            <Menu
              trigger={t('pinned.post_unpin_button')}
              triggerProps={{ className: 'text-sm font-normal text-text-on underline' }}
              items={(['community', 'topic'] as PostPinnedToType[])
                .filter(source => isPinnedIn(post, source))
                .map(source => ({
                  id: `unpin-target-${source}`,
                  label: (
                    <span className="flex w-full items-center gap-1.5">
                      <Pin02Icon className="size-5" />
                      {t(`community.actions.unpin_post.${source}`)}
                    </span>
                  ),
                  onSelect: () => {
                    void handleUnpin(post, source)
                  },
                }))}
            />
          </div>
        )}
      </div>
    </div>
  )
}
